/**
 * Guided weighing for one line (research/09 §3.3), as a pure reducer:
 *
 *   waiting -> adding -> settling -> in_tolerance (countdown) -> recorded
 *                              \-> overshoot (over tolerance and stable for 0.5 s)
 *
 * Weighing is cumulative: the line's mass is what it already had (top-ups) plus the reading minus the
 * baseline taken when the line started, so nobody re-tares between lines. A stable, in-tolerance reading
 * held for `autoAdvanceMs` records the line; Undo keeps the line open and holds auto-advance until the
 * reading leaves the tolerance band.
 */
import { Decimal, type DecimalString } from "@/lib/decimal";

export type WeighZone = "under" | "in" | "over";
export type WeighPhase = "idle" | "waiting" | "adding" | "settling" | "in_tolerance" | "overshoot" | "recorded";

export interface WeighLine {
  id: string;
  target: DecimalString;
  tolerance: DecimalString;
  /** Already in the vessel from an earlier weighing (a top-up after a rescale). */
  alreadyAdded: DecimalString;
}

export interface WeighSettings {
  /** 0 turns auto-advance off. */
  autoAdvanceMs: number;
  overshootMs: number;
}

export const DEFAULT_WEIGH_SETTINGS: WeighSettings = { autoAdvanceMs: 1200, overshootMs: 500 };

export interface RecordedWeighing {
  lineId: string;
  /** The line's total mass. */
  grams: DecimalString;
  /** The balance reading at the time (the next line's baseline). */
  reading: DecimalString;
  raw: string;
  stable: boolean;
}

export interface WeighState {
  phase: WeighPhase;
  line: WeighLine | null;
  baseline: DecimalString | null;
  reading: DecimalString | null;
  raw: string;
  stable: boolean;
  lineMass: DecimalString | null;
  /** Still to add (negative when over). */
  remaining: DecimalString | null;
  zone: WeighZone | null;
  stableSince: number | null;
  overSince: number | null;
  autoAdvanceAt: number | null;
  autoAdvanceHeld: boolean;
  recorded: RecordedWeighing | null;
}

export type WeighEvent =
  /** `holdAutoAdvance` restarts a line after Undo without recording it again straight away. */
  | { type: "start"; line: WeighLine; baseline: DecimalString | null; at: number; holdAutoAdvance?: boolean }
  | { type: "reading"; grams: DecimalString; stable: boolean; raw: string; at: number }
  | { type: "tick"; at: number }
  | { type: "rebase"; baseline: DecimalString; at: number }
  | { type: "undo"; at: number }
  | { type: "stop" };

export function initialWeighState(): WeighState {
  return {
    phase: "idle",
    line: null,
    baseline: null,
    reading: null,
    raw: "",
    stable: false,
    lineMass: null,
    remaining: null,
    zone: null,
    stableSince: null,
    overSince: null,
    autoAdvanceAt: null,
    autoAdvanceHeld: false,
    recorded: null,
  };
}

export function zoneOf(lineMass: DecimalString, target: DecimalString, tolerance: DecimalString): WeighZone {
  const difference = Decimal.from(lineMass).minus(target);
  if (difference.compare(tolerance) > 0) return "over";
  if (difference.negate().compare(tolerance) > 0) return "under";
  return "in";
}

function measure(state: WeighState): WeighState {
  const { line, baseline, reading } = state;
  if (!line || baseline === null || reading === null) return { ...state, lineMass: null, remaining: null, zone: null };
  const lineMass = Decimal.from(line.alreadyAdded).plus(reading).minus(baseline);
  return {
    ...state,
    lineMass: lineMass.toString(),
    remaining: Decimal.from(line.target).minus(lineMass).toString(),
    zone: zoneOf(lineMass.toString(), line.target, line.tolerance),
  };
}

function evaluate(state: WeighState, at: number, settings: WeighSettings): WeighState {
  if (!state.line || state.phase === "recorded" || state.zone === null) return state;
  const { zone, stable } = state;
  if (zone === "under") {
    return {
      ...state,
      phase: "adding",
      stableSince: null,
      overSince: null,
      autoAdvanceAt: null,
      autoAdvanceHeld: false,
    };
  }
  if (zone === "over") {
    if (!stable)
      return {
        ...state,
        phase: "settling",
        stableSince: null,
        overSince: null,
        autoAdvanceAt: null,
        autoAdvanceHeld: false,
      };
    const overSince = state.overSince ?? at;
    const phase = at - overSince >= settings.overshootMs ? "overshoot" : "settling";
    return { ...state, phase, overSince, stableSince: null, autoAdvanceAt: null, autoAdvanceHeld: false };
  }
  if (!stable) return { ...state, phase: "settling", stableSince: null, overSince: null, autoAdvanceAt: null };
  const stableSince = state.stableSince ?? at;
  const autoAdvance = settings.autoAdvanceMs > 0 && !state.autoAdvanceHeld;
  const autoAdvanceAt = autoAdvance ? stableSince + settings.autoAdvanceMs : null;
  if (autoAdvanceAt !== null && at >= autoAdvanceAt && state.lineMass !== null && state.reading !== null) {
    return {
      ...state,
      phase: "recorded",
      stableSince,
      overSince: null,
      autoAdvanceAt,
      recorded: { lineId: state.line.id, grams: state.lineMass, reading: state.reading, raw: state.raw, stable: true },
    };
  }
  return { ...state, phase: "in_tolerance", stableSince, overSince: null, autoAdvanceAt };
}

export function weighReducer(
  state: WeighState,
  event: WeighEvent,
  settings: WeighSettings = DEFAULT_WEIGH_SETTINGS,
): WeighState {
  switch (event.type) {
    case "start": {
      const started: WeighState = {
        ...initialWeighState(),
        line: event.line,
        baseline: event.baseline,
        reading: state.reading,
        raw: state.raw,
        stable: state.stable,
        phase: event.baseline === null ? "waiting" : "adding",
        autoAdvanceHeld: event.holdAutoAdvance ?? false,
      };
      return evaluate(measure(started), event.at, settings);
    }
    case "reading": {
      const next: WeighState = {
        ...state,
        reading: event.grams,
        raw: event.raw,
        stable: event.stable,
        baseline: state.line && state.baseline === null ? event.grams : state.baseline,
      };
      return evaluate(measure(next), event.at, settings);
    }
    case "tick":
      return evaluate(state, event.at, settings);
    case "rebase":
      return evaluate(measure({ ...state, baseline: event.baseline, stableSince: null }), event.at, settings);
    case "undo":
      return evaluate(
        { ...state, phase: state.line ? "adding" : "idle", recorded: null, autoAdvanceHeld: true, stableSince: null },
        event.at,
        settings,
      );
    case "stop":
      return { ...initialWeighState(), reading: state.reading, raw: state.raw, stable: state.stable };
  }
}

/** Seconds left before auto-advance, for the countdown (null when not counting down). */
export function autoAdvanceRemainingMs(state: WeighState, now: number): number | null {
  if (state.phase !== "in_tolerance" || state.autoAdvanceAt === null) return null;
  return Math.max(0, state.autoAdvanceAt - now);
}
